import { useState, useCallback, memo } from "react";
import { Modal, Button } from "react-bootstrap";
import Cropper from "react-easy-crop";
import { Area } from "react-easy-crop/types";
import { getCroppedImg } from "../../utils/getCroppedImg";
import { useSaveUser } from "../../api/mutations/useSaveUser";
import { User } from "../../types/Types";
import { useQueryClient } from "@tanstack/react-query";
import { useAppContext } from "../../Context/AppContext";

interface UploadImageProps {
  showBannerModal: boolean;
  setShowBannerModal: (show: boolean) => void;
  userData?: User;
}

const MODAL_BODY_STYLE = { minHeight: 400 };

const CROPPER_CONTAINER_STYLE = {
  position: "relative" as const,
  width: "100%",
  height: 400,
  background: "#333",
};

const ASPECT_RATIO = 3 / 1;

const UploadImage = memo(({
  showBannerModal,
  setShowBannerModal,
  userData,
}: UploadImageProps) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const { objectId } = useAppContext();
  const queryClient = useQueryClient();

  const handleClose = useCallback(() => {
    setImageSrc(null);
    setCroppedAreaPixels(null);
    setShowBannerModal(false);
  }, [setShowBannerModal]);

  const { mutate: updateUser, isPending } = useSaveUser({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["user", objectId] });
      handleClose();
    },
  });

  const onCropComplete = useCallback((_: any, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const onFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageSrc(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels, "blob");
      const file = new File([blob], "banner.jpg", { type: "image/jpeg" });

      const formData = new FormData();
      formData.append("bannerImage", file);

      Object.entries(userData || {}).forEach(([key, value]) => {
        if (key !== "bannerImage" && value !== null && value !== undefined) {
          if (typeof value === "object") {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, String(value));
          }
        }
      });

      updateUser(formData);
    } catch (error) {
      console.error("Failed to upload banner:", error);
    }
  }, [imageSrc, croppedAreaPixels, userData, updateUser]);

  return (
    <Modal centered show={showBannerModal} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Upload Banner</Modal.Title>
      </Modal.Header>
      <Modal.Body style={MODAL_BODY_STYLE}>
        {!imageSrc ? (
          <input
            type="file"
            accept="image/*"
            className="form-control"
            onChange={onFileChange}
          />
        ) : (
          <div style={CROPPER_CONTAINER_STYLE}>
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={ASPECT_RATIO}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        {imageSrc && (
          <Button
            type="button"
            variant="primary"
            onClick={handleSave}
            disabled={isPending}
          >
            {isPending ? "Saving..." : "Save"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
});
UploadImage.displayName = "UploadImage";

export default UploadImage;
